// Lifetime, the panel's sections, the strip and the Methods section (features/lifetime.ts drives the viewport).
// - Growth: the clock's age from the run's own age table (gestational weeks for the fetal atlas, weeks postmenstrual age for the
//   neonatal, by the scene's Period choice), the atlas's white-matter surface area there, and on the model track the model's growth
//   factor and section areas. The neonatal period has no model track and no maps: those sections say so or stand aside.
// - Surface: what the surface is lit by (plain, sulcal depth, curvature) over a fixed scale, and what the viewport is doing.
// - Track: the measured atlas, the model (LIFE-014's Newton fold) beside it in the strip, or the assigned thickness (LIFE-020's four
//   fine-mesh sections on an imposed-growth axis of their own, lifetime-thickness-sections-v0: a growth control and a thickness picker,
//   accepted prefixes only, blended between saved states, nothing past a track's range unless its last accepted state is asked for,
//   the whole-set verdict in the panel and Methods); the 3D stays the atlas.
// The strip under the viewport charts the templates' own area or enclosed volume by week, the cursor at the clock, or
// overlays the model's section on the atlas's own section at the same clock (lifetime-sections-v0: RAS y,z at one scale,
// no registration; the atlas shown at its nearest sampled week, the model blended between its saved states).
// - Parkinson's stages (the scene's still view on the MNI template, LIFE-019, lifetime-regions-v0): the Stage and Track
//   sections pick an ordinal Hoehn and Yahr stage and whether the cortex shows ENIGMA-PD's measured thinning there or the
//   killed connectome model's sample; a Comparison section plots the model against the measurement region by region at that
//   stage. The fetal sections, strip and clock stand aside while it's on.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Pause, Play } from 'lucide-react';
import { useAtlas, type SimSlot } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import { timelineOf } from '../app/scene.ts';
import { IconButton, Section, Segmented, Slider, type SegmentOption } from '../ui/primitives.tsx';
import { Chart, type ChartModel, type Series } from '../ui/Chart.tsx';
import { formatValue } from '../ui/chart.ts';
import { useLoaded } from '../ui/hooks.ts';
import { colormapStops, gradientCss, SERIES } from '../ui/scales.ts';
import { prettyName } from '../data/sims/methods.ts';
import { WEEK_S, type LifetimeMetric, type LifetimeSim } from '../data/sims/lifetime.ts';
import type { SectionsSim } from '../data/sims/lifetimeSections.ts';
import { GROWTH_STEP, loopArea, type ThicknessSectionsSim } from '../data/sims/lifetimeThicknessSections.ts';
import { dkRegionName, extremes, stageName, type RegionsSim } from '../data/sims/lifetimeRegions.ts';
import { parcelSummary, type ThicknessParcel, type ThicknessSex } from '../data/sims/lifetimeThickness.ts';
import { ageYears, METRIC_NAMES, type CentileMetric, type CentilesSim } from '../data/sims/lifetimeCentiles.ts';
import { LIFETIME_ASSIGNED, LIFETIME_CENTILES, LIFETIME_MODEL, LIFETIME_PD, LIFETIME_THICKNESS } from '../scenarios/index.ts';
import { Frame, SwatchKey } from './Traces.tsx';
import { Ref } from './Methods.tsx';
import {
  GROWTH_UI_STEP,
  LIFE_020B_ADDENDUM,
  MAPS,
  PD_VERDICT_LINE,
  SEX_COLOURS,
  STAGE_RANGE,
  STAGE_STEP,
  THICKNESS_AGES,
  THICKNESS_DIFF_LINE,
  THICKNESS_DIFF_RANGE,
  ageShort,
  ageWords,
  centilesModel,
  isNeonatal,
  lifetimeActive,
  litMap,
  mapOf,
  openCentiles,
  pdFrameAt,
  pdView,
  perWeek,
  spearman,
  thicknessView,
  useLifetime,
  useLifetimeDrive,
  usePdDrive,
  useThicknessDrive,
  type Beyond,
  type LifetimeStatus,
  type MapId,
  type PdStatus,
  type PdTrack,
  type ThicknessMode,
  type ThicknessStatus,
  type Track,
} from './lifetime.ts';
import './web.css';

const MAP_OPTIONS = MAPS.map((m) => ({ value: m.id, label: m.short, hint: m.name }));
const TRACK_OPTIONS: { value: Track; label: string; hint: string }[] = [
  { value: 'atlas', label: 'Measured atlas', hint: 'The dHCP atlas: the 3D surface and its own area' },
  { value: 'model', label: 'Model', hint: 'LIFE-014’s Newton fold, a 2D section over the atlas’s own' },
  { value: 'assigned', label: 'Assigned thickness', hint: 'LIFE-020: four assigned layer thicknesses on an imposed-growth axis, 2D sections at a growth of their own' },
];
const BEYOND_OPTIONS: SegmentOption<Beyond>[] = [
  { value: 'none', label: 'Nothing', hint: 'No data past a track’s accepted range: the contract’s rule' },
  { value: 'last', label: 'Last accepted', hint: 'The track’s last accepted state, dashed and labelled as such' },
];
/** A growth for the panel and the key: two decimals, or the three or four a 1/16 or 1/64 step needs ("2.00", "2.0625", "2.0156"). */
const fmtG = (g: number): string => {
  const s = g.toFixed(4).replace(/0+$/, '');
  const dot = s.indexOf('.');
  const decimals = dot < 0 ? -1 : s.length - dot - 1;
  return dot < 0 ? `${s}.00` : decimals < 2 ? `${s}${'0'.repeat(2 - decimals)}` : s;
};
/** The four tracks' colours in the strip (preview.py's order: cyan, green, amber, pink), the thinnest first. */
const TRACK_COLOURS = [SERIES[2]!, SERIES[6]!, SERIES[4]!, SERIES[3]!];
/** A scale's end: three significant figures at most, a real minus. */
const tick = (v: number): string => `${+v.toPrecision(3)}`.replace(/^-/, '−');
const HEIGHT = 168;
/** The cursor for the strip, whose x is scene time (stable, so the chart doesn't repaint on every render). */
const sceneTime = (t: number) => t;
/** The atlas's loops and the model's outline, on the strip's series colours. */
const ATLAS_COLOUR = SERIES[0]!;
const MODEL_COLOUR = SERIES[1]!;

/** The sequence the scene opened in its main slot, following the slot's path. */
function useSequence(): LifetimeSim | null {
  const path = useAtlas((s) => s.sims.slots.find((x) => x.slot === 'main')?.path ?? null);
  const o = openedSlot('main');
  return path && o?.format === 'lifetime' && o.path === path ? o.sim : null;
}

/** The model track's sections, when the scene found its run. */
function useModel(): SectionsSim | null {
  const path = useAtlas((s) => s.sims.slots.find((x) => x.slot === 'model')?.path ?? null);
  const o = openedSlot('model');
  return path && o?.format === 'sections' && o.path === path ? o.sim : null;
}

/** The assigned-thickness run's sections (LIFE-020), when the scene found its run. */
function useAssigned(): ThicknessSectionsSim | null {
  const path = useAtlas((s) => s.sims.slots.find((x) => x.slot === 'assigned')?.path ?? null);
  const o = openedSlot('assigned');
  return path && o?.format === 'thickness-sections' && o.path === path ? o.sim : null;
}

/** A value at `t` from values at `times`: linear between the two around it, clamped; null where either is missing. */
function at(times: ArrayLike<number>, values: (number | null | undefined)[], t: number): number | null {
  const n = times.length;
  if (!n) return null;
  if (n === 1 || t <= times[0]!) return values[0] ?? null;
  if (t >= times[n - 1]!) return values[n - 1] ?? null;
  let i = 1;
  while (i < n - 1 && times[i]! < t) i++;
  const a = values[i - 1];
  const b = values[i];
  if (a === null || a === undefined || b === null || b === undefined) return null;
  return a + ((t - times[i - 1]!) / (times[i]! - times[i - 1]!)) * (b - a);
}

const numberOf = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
/** Ages in words: "37, 40 and 44". */
const listWeeks = (a: readonly number[]): string => (a.length > 1 ? `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}` : `${a[0] ?? ''}`);
/** The pack a run names (lifetime-sim-v0's `pack`), for Methods. */
const packOf = (h: { pack?: unknown }): { id: string; version: string } | null => {
  const p = h.pack;
  if (!p || typeof p !== 'object' || typeof (p as { id?: unknown }).id !== 'string') return null;
  const v = (p as { version?: unknown }).version;
  return { id: (p as { id: string }).id, version: typeof v === 'string' ? v : '' };
};

function statusLine(status: LifetimeStatus, why: string | null, sim: LifetimeSim | null): string {
  if (status === 'loading') return 'Reading the sequence: its triangles first, then the two frames around the clock…';
  if (status === 'unsupported' || status === 'error') return why ?? 'The viewport refused the sequence.';
  if (status === 'on')
    return sim
      ? isNeonatal(sim.header)
        ? `${sim.times.length} frames at ${listWeeks(sim.ages)} weeks postmenstrual age, ${sim.counts.surfaces} surfaces moving on the pack’s own white matter, both hemispheres in one display frame; the motion between ages is interpolated, not growth.`
        : `${sim.times.length} weekly frames, ${sim.counts.surfaces} surfaces moving on the pack’s own white matter; the frames between weeks are interpolated, not grown.`
      : 'The surfaces are moving on the pack’s own white matter.';
  return '';
}

/** The Growth, Surface and Track sections, on the Lifetime scene, or the Parkinson's stages view's own sections. The drive
 *  hooks run here, as the panel stays mounted. */
export function LifetimeSections() {
  useLifetimeDrive();
  usePdDrive();
  useThicknessDrive();
  const pd = useAtlas(pdView);
  const th = useAtlas(thicknessView);
  if (th) return <ThicknessSections />;
  if (pd) return <PdSections />;
  return <FetalSections />;
}

/** The fetal sequence's sections. */
function FetalSections() {
  const active = useAtlas(lifetimeActive);
  const time = useAtlas((s) => s.time);
  const map = useLifetime((s) => s.map);
  const track = useLifetime((s) => s.track);
  const status = useLifetime((s) => s.status);
  const why = useLifetime((s) => s.why);
  const setMap = useLifetime((s) => s.setMap);
  const setTrack = useLifetime((s) => s.setTrack);
  const sim = useSequence();
  const model = useModel();
  const assigned = useAssigned();
  const growth = useLifetime((s) => s.growth);
  const thickness = useLifetime((s) => s.thickness);
  const beyond = useLifetime((s) => s.beyond);
  const setGrowth = useLifetime((s) => s.setGrowth);
  const setThickness = useLifetime((s) => s.setThickness);
  const setBeyond = useLifetime((s) => s.setBeyond);
  const areas = useLoaded(() => (sim ? sim.areaByFrame() : null), [sim]);
  const metrics = useLoaded(() => (model ? model.metrics() : null), [model]);
  const aMetrics = useLoaded(() => (assigned ? assigned.metrics() : null), [assigned]);
  const onAssigned = track === 'assigned' && !!assigned;
  // Playback along the growth axis: a quarter step every 60 ms to the axis's end (the strip blends between saved states). The
  // axis is the model's own, so this never touches the scene's clock.
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    if (!playing || !onAssigned || !assigned) return;
    const hi = assigned.range[1];
    const id = window.setInterval(() => {
      const g = useLifetime.getState().growth;
      if (g >= hi - 1e-9) setPlaying(false);
      else useLifetime.getState().setGrowth(Math.min(hi, g + GROWTH_UI_STEP));
    }, 60);
    return () => window.clearInterval(id);
  }, [playing, onAssigned, assigned]);
  if (!active) return null;
  const neonatal = isNeonatal(sim?.header);
  // The maps the run publishes (the fetal atlas's depth and curvature; the neonatal atlas none): the panel offers those, and the
  // surface is lit by the chosen one only where the run has it.
  const mapOptions = sim ? MAP_OPTIONS.filter((o) => !mapOf(o.value).quantity || sim.maps.some((x) => x.name === mapOf(o.value).quantity)) : MAP_OPTIONS;
  const m = litMap(mapOf(map), sim);
  const age = sim ? sim.ageAt(time) : null;
  const area = sim && areas.value ? at(sim.times, areas.value, time) : null;
  const first = areas.value?.[0] ?? null;
  const line = statusLine(status, why, sim);
  const onModel = track === 'model' && !!model;
  const modelNow = model ? model.modelAt(time) : null;
  const atlasNow = model ? model.atlasAt(time) : null;
  const modelArea = model && metrics.value ? at(model.model.times, metrics.value.model.map((r) => numberOf(r.section_area_mm2)), time) : null;
  const atlasArea = atlasNow && metrics.value ? numberOf(metrics.value.atlas[atlasNow.index]?.all_loop_unsigned_area_sum_mm2) : null;
  const verdicts = model?.header.verdicts;
  const trackOptions = TRACK_OPTIONS.filter((o) => (o.value !== 'model' || !!model) && (o.value !== 'assigned' || !!assigned));
  const aTrack = assigned ? (assigned.trackAt(thickness) ?? assigned.tracks[0] ?? null) : null;
  const aSection = assigned && aTrack ? assigned.sectionAt(aTrack, growth) : null;
  const [g0, g1] = assigned?.range ?? [1, 2.6875];
  const aGate = aMetrics.value?.gate ?? null;
  const aFailing = aGate ? Object.entries(aGate.gates).filter(([, v]) => !v).map(([k]) => k) : [];
  return (
    <>
      <Section title="Growth">
        <p className="lifetime-age" aria-live="off">
          {age !== null ? (
            <>
              <strong className="num">{age.toFixed(1)}</strong> {ageWords(sim?.header)}
            </>
          ) : (
            'Reading the atlas…'
          )}
        </p>
        <div className="rows">
          <div className="row">
            <span>White-matter surface</span>
            <span className="num">{area !== null ? `${formatValue(area / 100)} cm²` : '—'}</span>
          </div>
          <div className="row">
            <span>{`Since ${sim?.ages[0] ?? 21} weeks`}</span>
            <span className="num">{area !== null && first ? `× ${(area / first).toFixed(2)}` : '—'}</span>
          </div>
          {onModel && modelNow ? (
            <>
              <div className="row">
                <span>Model growth</span>
                <span className="num">{modelNow.growth !== null ? `g ${modelNow.growth.toFixed(2)}` : '—'}</span>
              </div>
              <div className="row">
                <span>Section area · model</span>
                <span className="num">{modelArea !== null ? `${formatValue(modelArea / 100)} cm²` : '—'}</span>
              </div>
              <div className="row">
                <span>{`Section area · atlas at ${atlasNow?.age ?? '?'} wk`}</span>
                <span className="num">{atlasArea !== null ? `${formatValue(atlasArea / 100)} cm²` : '—'}</span>
              </div>
            </>
          ) : null}
          {onAssigned && assigned && aTrack ? (
            <>
              <div className="row">
                <span>Imposed growth</span>
                <span className="num la-growth">
                  {`g ${fmtG(growth)}`}
                  <IconButton icon={playing ? Pause : Play} label={playing ? 'Pause the growth' : 'Play the growth'} onClick={() => setPlaying((p) => !p)} />
                </span>
              </div>
              <div className="pd-step la-step">
                <span className="value ba-type-small">{fmtG(g0)}</span>
                <Slider value={growth} min={g0} max={g1} step={GROWTH_UI_STEP} onChange={(v) => setGrowth(v)} label="Imposed growth" />
                <span className="value ba-type-small">{fmtG(g1)}</span>
              </div>
              <div className="row">
                <span>Thickness</span>
                <Segmented
                  size="sm"
                  label="Assigned thickness"
                  value={String(aTrack.thicknessMm)}
                  options={assigned.thicknesses.map((h) => ({ value: String(h), label: `${h} mm`, hint: assigned.trackAt(h)?.label ?? `h = ${h} mm` }))}
                  onChange={(v) => setThickness(Number(v))}
                />
              </div>
              <div className="row">
                <span>State</span>
                <span className="num la-state">
                  {aSection ? (aSection.exact ? 'saved state' : `between ${fmtG(aTrack.growth[aSection.i]!)} and ${fmtG(aTrack.growth[aSection.j]!)}`) : aTrack.frames ? 'no accepted state' : 'never started'}
                </span>
              </div>
              <div className="row">
                <span>Section area · display-derived</span>
                <span className="num la-area">{aSection ? `${formatValue(loopArea(aSection.loop) / 100)} cm²` : '—'}</span>
              </div>
              <div className="row">
                <span>Accepted states</span>
                <span className="num la-accepted">{`${aTrack.frames} of ${assigned.requested?.length ?? '?'} · ${aTrack.caseStatus.replace(/_/g, ' ')}${aTrack.failedAt !== null ? ` · rejected at g ${fmtG(aTrack.failedAt)}` : ''}`}</span>
              </div>
              <div className="row">
                <span>Past range</span>
                <Segmented size="sm" label="Past a track’s accepted range" value={beyond} options={BEYOND_OPTIONS} onChange={(v: Beyond) => setBeyond(v)} />
              </div>
            </>
          ) : null}
        </div>
        <p className="live-hint">
          {onAssigned
            ? 'Imposed tangential growth is the model’s own axis: no age, no seconds, no clock, and nothing maps it to the atlas’s weeks. Between two saved states the outline is blended for display, not solved; past a track’s accepted range there is no data, and never anything between thicknesses. The section area is the polygon area of the outline drawn, not one of the run’s measures.'
            : onModel
            ? 'The model’s growth factor is its assumed clock (g = 1 + (age − 21) / 15), not measured timing; the section areas are polygon areas of the outlines the strip draws, the atlas’s over all its loops at the sampled week.'
            : neonatal
              ? 'Three population templates (dHCP, 37, 40 and 44 weeks postmenstrual age), one surface a hemisphere, placed together in one display frame; the area is the templates’ own, both hemispheres together, interpolated between ages as the surface is drawn. Nothing joins this sequence to the fetal one.'
              : 'Sixteen weekly population templates (dHCP, 242 scans), one surface a hemisphere; the area is the templates’ own, both hemispheres together, interpolated between weeks as the surface is drawn.'}
        </p>
      </Section>
      <Section title="Surface">
        <div className="rows">
          <div className="row">
            <span>Light by</span>
            {mapOptions.length > 1 ? <Segmented size="sm" label="Light the surface by" value={m.id} options={mapOptions} onChange={(v: MapId) => setMap(v)} /> : <span className="lifetime-track">Plain</span>}
          </div>
        </div>
        {m.quantity && m.range && m.colormap ? (
          <div className="colorbar lifetime-legend" role="img" aria-label={`${m.name}, ${tick(m.range[0])} to ${tick(m.range[1])} ${m.units}, as colour over the surface`}>
            <span className="name ba-type-small">{m.name}</span>
            <span className="strip" style={{ background: gradientCss(colormapStops(m.colormap)) }} />
            <span className="ticks ba-type-caption">
              <span>{`${tick(m.range[0])} ${m.units}`}</span>
              <span>{`${tick(m.range[1])} ${m.units}`}</span>
            </span>
          </div>
        ) : null}
        <p className="live-hint">{mapOptions.length > 1 ? m.hint : 'This atlas publishes no maps: the surface is in the tissue’s own look, the folds read from its shape and shading alone.'}</p>
        {line ? (
          <p className={`live-hint lifetime-status ${status}`} aria-live="polite">
            {line}
          </p>
        ) : null}
      </Section>
      {neonatal ? null : (
      <Section title="Track">
        <div className="rows">
          <div className="row">
            <span>Showing</span>
            {trackOptions.length > 1 ? <Segmented size="sm" label="Track" value={track} options={trackOptions} onChange={(v: Track) => setTrack(v)} /> : <span className="lifetime-track">Measured atlas</span>}
          </div>
        </div>
        <p className="live-hint">
          {onAssigned && assigned
            ? `LIFE-020’s assigned-thickness sections: ${assigned.tracks.length} fine-mesh cases of the same 21-week white-matter section at assigned layer thicknesses of ${assigned.thicknesses.join(', ')} mm, each played only through the growth states its case accepted (${assigned.tracks.map((t) => `${t.thicknessMm} mm: ${t.frames}`).join(', ')} of ${assigned.requested?.length ?? '?'}), drawn in the strip on one physical scale with the chosen thickness in front. The 3D stays the atlas.`
            : !model
            ? 'The model track, LIFE-014’s Newton fold grown from the 21-week surface, joins beside the atlas when its run is published; the two are then labelled measured atlas and model.'
            : onModel
              ? `LIFE-014’s Newton fold: ${model.model.times.length} saved states of a plane-strain section grown from the 21-week atlas section, blended between states for display, drawn in the strip over the atlas’s own section at its nearest sampled week. The 3D surface stays the measured atlas.`
              : 'The dHCP atlas, measured: the 3D surface and the strip’s area are its own. Model draws LIFE-014’s section beside the atlas’s in the strip.'}
        </p>
        {model && onModel && verdicts ? (
          <p className="live-hint lifetime-verdicts">{['LIFE-014', 'LIFE-015'].map((k) => (verdicts[k] ? `${k}: ${verdicts[k]}` : null)).filter(Boolean).join(' · ')}</p>
        ) : null}
        {onAssigned && assigned ? (
          <p className="live-hint lifetime-verdicts la-verdict">
            {`LIFE-020: ${assigned.verdict}${aGate ? (aGate.passed ? ' · whole-set numerical gate passed' : aGate.passed === false ? ` · whole-set numerical gate failed${aFailing.length ? ` (${aFailing.join(', ')})` : ''}, nothing scored` : '') : ''} · the whole set’s registered verdict, copied by hash`}
          </p>
        ) : null}
      </Section>
      )}
    </>
  );
}

// Parkinson's stages.

const PD_TRACK_OPTIONS: { value: PdTrack; label: string; hint: string }[] = [
  { value: 'measured', label: 'Measured (ENIGMA-PD)', hint: 'ENIGMA-PD’s published cortical thinning at the exact stage, patients against controls' },
  { value: 'model', label: 'Model', hint: 'The connectome diffusion model’s sample at the step, LIFE-019 (killed)' },
];
/** The Stage radios on the measured track: one per published stage, by label. */
const STAGE_OPTIONS = (labels: string[]): SegmentOption<string>[] => labels.map((l, i) => ({ value: `${i}`, label: l, hint: stageName(l) }));
/** A signed effect size for the panel: two decimals, a real minus and a plus. */
const signed = (v: number, digits = 2): string => (v < 0 ? `−${Math.abs(v).toFixed(digits)}` : `+${v.toFixed(digits)}`);
/** A structure's name from the catalogue, or its id spelled out. */
const nameOf = (id: string, names: Map<string, { name?: string }> | undefined): string => names?.get(id)?.name ?? id.replace(/^ba:/, '').replace(/-/g, ' ');

function pdStatusLine(status: PdStatus, why: string | null, track: PdTrack): string {
  if (status === 'loading') return 'Reading the stage maps…';
  if (status === 'error') return why ?? 'The viewport refused the stage map.';
  if (status === 'on') return track === 'measured' ? 'The cortex carries the published stage’s thinning; the no-data regions keep its own shade.' : 'The cortex carries the model’s sample at this step.';
  return '';
}

/** The no-data regions in words, each once with its sides ("banks of the superior temporal sulcus, both sides"). */
export function noDataLine(dk: string[]): string {
  const sides = new Map<string, Set<string>>();
  for (const d of dk) {
    const m = /^([LR])_(.+)$/.exec(d);
    const base = m ? m[2]! : d;
    if (!sides.has(base)) sides.set(base, new Set());
    sides.get(base)!.add(m ? m[1]! : '');
  }
  return [...sides].map(([base, s]) => `${dkRegionName(base)}${s.has('L') && s.has('R') ? ', both sides' : s.has('L') ? ', left' : s.has('R') ? ', right' : ''}`).join('; ');
}

/** The Stage, Track and Comparison sections of the Parkinson's stages view. */
function PdSections() {
  const sim = useLifetime((s) => s.pd);
  const track = useLifetime((s) => s.pdTrack);
  const stage = useLifetime((s) => s.stage);
  const status = useLifetime((s) => s.pdStatus);
  const why = useLifetime((s) => s.pdWhy);
  const setTrack = useLifetime((s) => s.setPdTrack);
  const setStage = useLifetime((s) => s.setStage);
  const names = useAtlas((s) => s.catalogue?.byId);
  const shown = sim ? pdFrameAt(sim, track, stage) : null;
  const frame = useMemo(() => (sim && shown ? (track === 'measured' ? sim.measured : sim.model).frame(shown.index) : null), [sim, track, shown?.index]); // eslint-disable-line react-hooks/exhaustive-deps -- the frame is a function of the track and the index
  const ex = frame && sim ? extremes(frame, sim.structureIds) : null;
  const range = sim?.range ?? [-0.6, 0.6];
  const labels = sim?.stageLabels ?? ['HY1', 'HY2', 'HY3', 'HY4/5'];
  const measuredIndex = sim ? sim.measured.nearest(stage) : Math.round(stage);
  const nrmse = sim?.header.validation?.primary?.model?.nrmse;
  return (
    <>
      <Section title="Stage">
        <div className="rows">
          <div className="row">
            <span>{track === 'measured' ? 'Hoehn and Yahr' : 'Step'}</span>
            {track === 'measured' ? (
              <Segmented size="sm" label="Stage" value={`${measuredIndex}`} options={STAGE_OPTIONS(labels)} onChange={(v) => setStage(sim ? sim.measured.steps[Number(v)]! : Number(v))} />
            ) : (
              <span className="num pd-shown">{shown ? `${shown.step.toFixed(2)} · ${shown.label}` : '—'}</span>
            )}
          </div>
        </div>
        {track === 'model' ? (
          <div className="pd-step">
            <span className="value ba-type-small">{labels[0]}</span>
            <Slider value={stage} min={STAGE_RANGE[0]} max={STAGE_RANGE[1]} step={STAGE_STEP} onChange={(v) => setStage(v)} label="Model step" />
            <span className="value ba-type-small">{labels[labels.length - 1]}</span>
          </div>
        ) : null}
        <p className="pd-stage-line ba-type-small" aria-live="off">
          {shown ? (track === 'measured' ? stageName(shown.label) : `The model’s sample nearest ${stage.toFixed(2)}; whole steps are the measured stages.`) : 'Reading the stage maps…'}
        </p>
        <p className="live-hint">An ordinal step on the Hoehn and Yahr scale, never age or time; the measured maps are exact stages, never interpolated.</p>
      </Section>
      <Section title="Track">
        <div className="rows">
          <div className="row">
            <span>Showing</span>
            <Segmented size="sm" label="Track" value={track} options={PD_TRACK_OPTIONS} onChange={(v: PdTrack) => setTrack(v)} />
          </div>
        </div>
        <div className="colorbar lifetime-legend pd-legend" role="img" aria-label={`Cortical thinning, d, ${tick(range[0])} to ${tick(range[1])}, thinning positive, as colour over the cortex`}>
          <span className="name ba-type-small">Cortical thinning · d</span>
          <span className="strip" style={{ background: gradientCss(colormapStops('coolwarm')) }} />
          <span className="ticks ba-type-caption">
            <span>{`${tick(range[0])} thicker`}</span>
            <span>{`${tick(range[1])} thinner`}</span>
          </span>
        </div>
        <dl className="dfc-facts pd-facts">
          <div>
            <dt>Most thinned</dt>
            <dd className="pd-most">{ex?.most ? `${nameOf(ex.most.id, names)} ${signed(ex.most.value)}` : '—'}</dd>
          </div>
          <div>
            <dt>Least</dt>
            <dd className="pd-least">{ex?.least ? `${nameOf(ex.least.id, names)} ${signed(ex.least.value)}` : '—'}</dd>
          </div>
          <div>
            <dt>Regions</dt>
            <dd className="pd-count">{sim && ex ? `${ex.finite} of ${sim.structureIds.length} shown · ${sim.noData.length} no data` : '—'}</dd>
          </div>
        </dl>
        <p className="live-hint">
          {track === 'measured'
            ? 'Signed Cohen-type mixed-effects d, patients at the stage against controls (Laansma 2021, ENIGMA-PD), on the Desikan–Killiany regions; drawn on the template’s same-name DKT structures, whose boundaries differ.'
            : `Normative structural graph diffusion with global amplification, seeded at HY1 and fitted at HY2 only; the registered HY3 test killed it${typeof nrmse === 'number' ? ` (NRMSE ${Math.round(nrmse * 100)} % against the registered 20 %)` : ''}.`}
        </p>
        {sim ? <p className="live-hint pd-nodata">{`No data (no one-to-one DKT counterpart, never interpolated): ${noDataLine(sim.noData)}.`}</p> : null}
        {pdStatusLine(status, why, track) ? (
          <p className={`live-hint lifetime-status pd-status ${status}`} aria-live="polite">
            {pdStatusLine(status, why, track)}
          </p>
        ) : null}
      </Section>
      {sim ? (
        <Section title="Comparison">
          <PdScatter sim={sim} stage={stage} names={names} />
          <p className="live-hint pd-verdict">{PD_VERDICT_LINE}</p>
        </Section>
      ) : null}
    </>
  );
}

/** The model's sample against the measurement, region by region, at the measured stage nearest the step. */
function PdScatter({ sim, stage, names }: { sim: RegionsSim; stage: number; names: Map<string, { name?: string }> | undefined }) {
  const mi = sim.measured.nearest(stage);
  const measured = useMemo(() => sim.measured.frame(mi), [sim, mi]);
  const modelIndex = sim.model.nearest(sim.measured.steps[mi]!);
  const model = useMemo(() => sim.model.frame(modelIndex), [sim, modelIndex]);
  const [lo, hi] = sim.range;
  const size = 168;
  const pad = 6;
  const px = (v: number) => pad + ((Math.min(hi, Math.max(lo, v)) - lo) / (hi - lo)) * (size - 2 * pad);
  const py = (v: number) => size - px(v);
  const rho = spearman(measured, model);
  const label = sim.stageLabels[mi] ?? `stage ${mi}`;
  let worst: { id: string; gap: number } | null = null;
  for (let k = 0; k < sim.structureIds.length; k++) {
    const gap = Math.abs(model[k]! - measured[k]!);
    if (Number.isFinite(gap) && (!worst || gap > worst.gap)) worst = { id: sim.structureIds[k]!, gap };
  }
  return (
    <div className="pd-scatter">
      <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`The model’s thinning against the measured thinning at ${label}, one dot a region, over ${tick(lo)} to ${tick(hi)} d both ways; the dashed line is agreement`}>
        <rect className="frame" x={pad} y={pad} width={size - 2 * pad} height={size - 2 * pad} />
        <line className="agree" x1={px(lo)} y1={py(lo)} x2={px(hi)} y2={py(hi)} />
        <line x1={px(0)} y1={pad} x2={px(0)} y2={size - pad} opacity={0.5} />
        <line x1={pad} y1={py(0)} x2={size - pad} y2={py(0)} opacity={0.5} />
        {sim.structureIds.map((id, k) => (Number.isFinite(measured[k]) && Number.isFinite(model[k]) ? <circle key={id} className="dot" cx={px(measured[k]!)} cy={py(model[k]!)} r={2.4} fill={id.startsWith('ba:left-') ? ATLAS_COLOUR : MODEL_COLOUR} /> : null))}
        <text x={size - pad} y={size - pad - 3} textAnchor="end">{`measured, ${label} →`}</text>
        <text x={pad + 3} y={pad + 9}>model ↑</text>
      </svg>
      <div className="pd-key ba-type-small">
        <p>
          <span className="lead">{`At ${label}`}</span>
          {` · ${sim.structureIds.length} regions, each the model’s step ${sim.model.steps[modelIndex]!.toFixed(2)} sample against ENIGMA-PD’s measurement`}
          {rho !== null ? ` · Spearman ρ ${rho.toFixed(2)}` : ''}
          {worst ? ` · widest gap ${nameOf(worst.id, names)} (${worst.gap.toFixed(2)} d)` : ''}
        </p>
        <ul className="ls-key ba-type-small">
          <li>
            <span className="swatch" style={{ background: ATLAS_COLOUR }} />
            <span>Left hemisphere</span>
          </li>
          <li>
            <span className="swatch" style={{ background: MODEL_COLOUR }} />
            <span>Right hemisphere</span>
          </li>
        </ul>
        <p className="ba-type-caption muted">The dashed line is agreement. HY2 is the fit stage, so its dots are close by construction; HY3 is the registered test the model failed, HY1 its seed (the model equals the measurement there), HY4/5 beyond the fit.</p>
      </div>
    </div>
  );
}

const pct = (v: unknown): string => (typeof v === 'number' && Number.isFinite(v) ? `${Math.round(v * 100)} %` : '—');
const dec = (v: unknown, digits = 3): string => (typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—');
const BASELINE_NAMES: Record<string, string> = { model: 'Connectome model', distance: 'Distance only', no_spread: 'No spread', regional_linear: 'Regional linear', zero: 'Zero', spatial_mean: 'Spatial mean' };

/** The Methods sheet's section for the Parkinson's stages view: the run, its tracks, mapping, model, validation and ba-life's own Methods verbatim. */
function PdMethods() {
  const sim = useLifetime((s) => s.pd);
  const status = useLifetime((s) => s.pdStatus);
  const why = useLifetime((s) => s.pdWhy);
  const track = useLifetime((s) => s.pdTrack);
  const stage = useLifetime((s) => s.stage);
  if (!sim) {
    return (
      <section className="methods-sim methods-lifetime-pd">
        <h5 className="caps">Parkinson’s stages</h5>
        <dl>
          <dt>Run</dt>
          <dd>
            <span className="mono">{LIFETIME_PD}</span>
          </dd>
          <dt>Drawn</dt>
          <dd>{status === 'error' ? (why ?? 'Not read.') : 'Reading the stage maps…'}</dd>
        </dl>
      </section>
    );
  }
  const h = sim.header;
  const shown = pdFrameAt(sim, track, stage);
  const primary = h.validation?.primary ?? {};
  const inputs = h.inputs?.source_sha256 && typeof h.inputs.source_sha256 === 'object' ? Object.keys(h.inputs.source_sha256 as Record<string, string>) : [];
  return (
    <section className="methods-sim methods-lifetime-pd">
      <h5 className="caps">Parkinson’s stages</h5>
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{LIFETIME_PD}</span> <span className="muted">{`${h.kind ?? 'comparison'} · ${h.provenance ?? 'derived'}`}</span>
        </dd>
        <dt>Measured track</dt>
        <dd>{`${h.tracks.measured.label}: ${sim.measured.frames} published stage maps (${sim.stageLabels.join(', ')}), ${h.units?.values ?? 'signed d, thinning positive'}, patients at the stage against controls (Laansma 2021). Shown one exact stage at a time, never interpolated.`}</dd>
        <dt>Model track</dt>
        <dd>{`${h.tracks.model.label}: ${sim.model.frames} samples at ordinal steps ${tick(sim.model.steps[0]!)} to ${tick(sim.model.steps[sim.model.frames - 1]!)}, ${h.tracks.model.interpolation ?? 'direct evaluations'}; the cortex shows the sample nearest the step, never an age.`}</dd>
        <dt>Model</dt>
        <dd>
          {h.model?.name ?? 'Connectome diffusion'}
          {h.model?.equation ? <div className="mono muted">{h.model.equation}</div> : null}
          {h.model?.parameters ? <div className="muted">{`β ${dec(h.model.parameters.beta, 2)}, gain a ${dec(h.model.parameters.gain, 3)}; fit at ${h.model.fit_stage ?? 'HY2'}; seeded at HY1.`}</div> : null}
        </dd>
        <dt>Output</dt>
        <dd>Derived: a comparison of measured group maps with a simulated extrapolation. Nothing here is a patient; the measured maps are population effects.</dd>
        <dt>Steps</dt>
        <dd>{`A stage is an ordinal step, never age or time (the run’s own words: ${h.units?.time ?? 'ordinal stage step'}); ${h.time?.origin ?? 'HY1'} is step 0, HY4/5 step 3. There is no clock in this view.`}</dd>
        <dt>Regions</dt>
        <dd>{`${sim.structureIds.length} Desikan–Killiany regions drawn on the template's same-name DKT structures (boundaries differ); ${sim.noData.length} DK-only regions (${sim.noData.map(dkRegionName).join(', ')}) have no counterpart and are shown as no data, never a neighbour's value. All 68 were scored.`}</dd>
        <dt>Drawn</dt>
        <dd>{status === 'on' ? `Through the viewport’s overlay, one frame: the ${track === 'measured' ? `measured ${shown.label} map` : `model’s step ${shown.step.toFixed(2)} sample`}, coolwarm over ${tick(sim.range[0])} to ${tick(sim.range[1])} d, no data as the cortex’s own shade.` : status === 'error' ? (why ?? 'Refused by the viewport.') : 'Loading.'}</dd>
        <dt>Validation</dt>
        <dd>
          <div>{`Verdict: ${h.validation?.verdict ?? 'unknown'}. Registered primary test at HY3, RMSE against a 20 % NRMSE rule.`}</div>
          {Object.entries(primary).map(([k, v]) => (
            <div key={k} className="muted">
              {`${BASELINE_NAMES[k] ?? k}: RMSE ${dec(v.rmse)} d, NRMSE ${pct(v.nrmse)}${typeof v.spearman === 'number' ? `, Spearman ${dec(v.spearman, 2)}` : ''}`}
            </div>
          ))}
        </dd>
        {inputs.length ? (
          <>
            <dt>Inputs</dt>
            <dd>{`ENIGMA toolbox (${inputs.length} files by hash): the HCP structural connectome and its cortical labels, the Desikan–Killiany parcellation on fsaverage5 and ENIGMA-PD’s summary statistics.`}</dd>
          </>
        ) : null}
        {h.pipeline?.producer ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`ba-life @ ${short(h.pipeline.producer)}`}</dd>
          </>
        ) : null}
      </dl>
      {h.methods ? <p>{h.methods}</p> : null}
    </section>
  );
}

/** Each hemisphere's area from the first source week to the last, for the sheet. */
function growthLine(rows: LifetimeMetric[]): string | null {
  const hemis = [...new Set(rows.map((r) => r.hemisphere))].sort();
  const parts = hemis.flatMap((h) => {
    const own = rows.filter((r) => r.hemisphere === h).sort((a, b) => a.week - b.week);
    const a = own[0];
    const b = own[own.length - 1];
    if (!a || !b || a === b) return [];
    return [`${prettyName(h).toLowerCase()} ${formatValue(a.area_mm2 / 100)} → ${formatValue(b.area_mm2 / 100)} cm² (× ${(b.area_mm2 / a.area_mm2).toFixed(1)}, ${a.week} → ${b.week} weeks)`];
  });
  return parts.length ? `White-matter surface area, the templates’ own: ${parts.join('; ')}.` : null;
}

const short = (sha: unknown): string => (typeof sha === 'string' ? sha.slice(0, 8) : '');

/** The model track's part of the sheet: its run, model, states, sections, inputs, verdicts and Methods, verbatim where they are words. */
function ModelMethods({ model }: { model: SectionsSim }) {
  const h = model.header;
  const mb = h.sections?.model;
  const ab = h.sections?.atlas;
  const planeX = model.model.planeX?.[0];
  const g = model.model.growth;
  return (
    <section className="methods-sim methods-lifetime-model">
      <h5 className="caps">Model track</h5>
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{LIFETIME_MODEL}</span> <span className="muted">{h.kind ?? 'simulated'}</span>
        </dd>
        <dt>Model</dt>
        <dd>
          {h.model?.name ?? 'Model'}
          {typeof (h.model as { solver?: unknown } | undefined)?.solver === 'string' ? <div className="muted">{`Solver: ${(h.model as { solver: string }).solver}`}</div> : null}
          {typeof (h.model as { bulk_to_shear_ratio?: unknown } | undefined)?.bulk_to_shear_ratio === 'number' ? <div className="muted">{`Bulk to shear ratio K/μ = ${(h.model as { bulk_to_shear_ratio: number }).bulk_to_shear_ratio}`}</div> : null}
        </dd>
        <dt>Output</dt>
        <dd>Simulated: a plane-strain section’s boundary, not a 3D surface. Its states between the saved ones are blended for display, not solved.</dd>
        <dt>States</dt>
        <dd>{`${model.model.times.length} saved states, ${model.model.points} boundary nodes each${g ? `, growth g ${g[0]} → ${g[g.length - 1]}` : ''}${h.growth?.clock ? ` (${h.growth.clock})` : ''}.`}</dd>
        <dt>Sections</dt>
        <dd>{`The model in its fixed ${mb?.plane?.axis ?? 'sagittal'} plane${planeX !== undefined ? ` at x ${tick(planeX)} mm` : ''} (${mb?.plane?.rule ?? 'the week-21 source plane'}); the atlas’s own sections at its ${model.atlas.times.length} sampled weeks, every loop, each at its mean-x plane (${ab?.plane?.rule ?? 'the mean x of the left surface at that week'}). Drawn in RAS y,z at one scale, without registration or rescaling.`}</dd>
        {h.inputs?.length ? (
          <>
            <dt>Inputs</dt>
            <dd>{h.inputs.map((x) => `${(x as { id?: string }).id ?? x.source ?? x.what ?? 'input'}${(x as { header_sha256?: unknown }).header_sha256 ? ` (${short((x as { header_sha256: string }).header_sha256)})` : ''}`).join('; ')}</dd>
          </>
        ) : null}
        {h.verdicts ? (
          <>
            <dt>Verdicts</dt>
            <dd>
              {Object.entries(h.verdicts).map(([k, v]) => (
                <div key={k}>
                  <span className="muted">{`${k}: `}</span>
                  {v}
                </div>
              ))}
            </dd>
          </>
        ) : null}
        {h.pipeline?.path ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`${h.pipeline.path}${h.pipeline.commit ? ` @ ${short(h.pipeline.commit)}` : ''}`}</dd>
          </>
        ) : null}
      </dl>
      {h.methods ? <p>{h.methods}</p> : null}
    </section>
  );
}

/** The Methods sheet's section for the sequence: its run, frames, surfaces and maps, what's drawn, and the contract's paragraphs. */
export function LifetimeMethods() {
  const pd = useAtlas(pdView);
  const th = useAtlas(thicknessView);
  const sim = useSequence();
  const model = useModel();
  const assigned = useAssigned();
  const map = useLifetime((s) => s.map);
  const status = useLifetime((s) => s.status);
  const chart = useLifetime((s) => s.chart);
  const centiles = useLifetime((s) => s.centiles);
  const rows = useLoaded(() => (sim ? sim.metrics() : null), [sim]);
  if (th) return <ThicknessMethods />;
  if (pd) return <PdMethods />;
  if (!sim) return null;
  const h = sim.header;
  const c = sim.counts;
  const m = litMap(mapOf(map), sim);
  const version = typeof h.version === 'string' ? h.version : null;
  const neonatal = isNeonatal(h);
  const pack = packOf(h);
  const span = `${sim.ages[0]}–${sim.ages[sim.ages.length - 1]} ${ageWords(h)}`;
  const growth = rows.value ? growthLine(rows.value) : null;
  const drawn =
    status === 'on'
      ? 'In the 3D viewport, through its morph layer: the pack’s own white-matter surfaces moved to the sequence’s frame at the clock.'
      : status === 'unsupported'
        ? 'Not in this viewport; the clock, the chart and the numbers are the atlas’s.'
        : status === 'error'
          ? 'Refused by the viewport.'
          : 'Loading.';
  return (
    <>
      <section className="methods-sim methods-lifetime">
        <h5 className="caps">{neonatal ? 'Derived reference' : 'Measured atlas'}</h5>
        <dl>
          <dt>Run</dt>
          <dd>
            <span className="mono">{`lifetime/${h.id}`}</span> {version ? <span className="muted mono">{version}</span> : null} <span className="muted">{h.kind}</span>
          </dd>
          {pack ? (
            <>
              <dt>Pack</dt>
              <dd>
                <span className="mono">{pack.id}</span> <span className="muted mono">{pack.version}</span>
                <span className="muted">{` · the geometry the surfaces are moved on, in ${h.space ?? 'its own space'}`}</span>
              </dd>
            </>
          ) : null}
          <dt>Model</dt>
          <dd>
            {h.model?.name ?? 'Population atlas'}
            {typeof (h.model as { type?: unknown } | undefined)?.type === 'string' ? <div className="muted">{(h.model as { type: string }).type}</div> : null}
            {h.model?.citation ? (
              <div className="muted">
                <Ref text={h.model.citation} />
              </div>
            ) : null}
          </dd>
          <dt>Output</dt>
          <dd>
            {neonatal
              ? 'Derived reference: dHCP’s population templates at three postmenstrual ages, assembled for display in a mirror-symmetry pose with the hemispheres placed 2 mm apart by convention (not measured anatomy or author coordinates); the motion between them is derived by interpolation, not individual growth. Nothing in this track is simulated, and nothing joins it to the fetal sequence.'
              : 'Reference: measured population templates, one a week, from in-utero scans; the frames between them are derived by interpolation. Nothing in this track is simulated.'}
          </dd>
          {h.inputs?.length ? (
            <>
              <dt>Source</dt>
              <dd>{h.inputs.map((x) => `${x.source ?? x.what ?? 'input'}${typeof (x as { commit?: unknown }).commit === 'string' ? ` · commit ${(x as { commit: string }).commit.slice(0, 8)}` : ''}`).join('; ')}</dd>
            </>
          ) : null}
          <dt>Frames</dt>
          <dd>
            {neonatal
              ? `${sim.times.length} templates at ${listWeeks(sim.ages)} weeks postmenstrual age; between two ages the positions and normals are interpolated linearly, so a frame’s own age draws that template exactly; one constant rigid transform per hemisphere places every age in the display frame, so the brain holds still.`
              : `${sim.times.length} weekly templates, ${span}; between two weeks the positions, normals and maps are interpolated linearly, so a frame’s own week draws that template exactly.`}
          </dd>
          <dt>Surfaces</dt>
          <dd>{`${c.surfaces} (${sim.surfaces.map((s) => s.name ?? s.id).join(', ')}): ${c.vertices.toLocaleString()} vertices and ${c.triangles.toLocaleString()} triangles, moved on the pack’s own white matter in ${h.space ?? 'their own space'}.`}</dd>
          <dt>Maps</dt>
          <dd>
            {sim.maps.length
              ? `${sim.maps.map((x) => `${prettyName(x.name.replace(/^source_/, ''))}${x.units ? ` (${x.units})` : ''}`).join(', ')}, the atlas’s own; ${m.quantity && m.range ? `the surface is lit by ${m.name.toLowerCase()} over ${tick(m.range[0])} to ${tick(m.range[1])} ${m.units}.` : 'the surface is drawn plain.'}`
              : 'None.'}
          </dd>
          {growth ? (
            <>
              <dt>Growth</dt>
              <dd>{growth}</dd>
            </>
          ) : null}
          <dt>Drawn</dt>
          <dd>{drawn}</dd>
          {h.pipeline?.path ? (
            <>
              <dt>Pipeline</dt>
              <dd className="mono">{h.pipeline.path}</dd>
            </>
          ) : null}
        </dl>
        {sim.paragraphs
          ? Object.entries(sim.paragraphs).map(([k, v]) => (
              <p key={k}>
                <span className="caps muted">{`${prettyName(k)}. `}</span>
                {v}
              </p>
            ))
          : null}
      </section>
      {model ? <ModelMethods model={model} /> : null}
      {assigned ? <AssignedMethods sim={assigned} /> : null}
      {chart === 'centiles' && centiles ? <CentilesMethods sim={centiles} /> : null}
    </>
  );
}

const METRIC_OPTIONS: SegmentOption<CentileMetric>[] = [
  { value: 'GMV', label: 'GMV', hint: METRIC_NAMES.GMV },
  { value: 'WMV', label: 'WMV', hint: METRIC_NAMES.WMV },
  { value: 'Ventricles', label: 'Ventricles', hint: METRIC_NAMES.Ventricles },
];

/** The Methods sheet's section for the chart track: the run, the model and its objects, the series, the native age and what isn't converted. */
function CentilesMethods({ sim }: { sim: CentilesSim }) {
  const h = sim.header;
  const [d0, d1] = sim.ageRange;
  const checks = h.validation?.checks ?? {};
  const worst = Math.max(0, ...Object.values(checks).flatMap((c) => c.cdf_roundtrip_max_abs ?? []));
  return (
    <section className="methods-sim methods-lifetime-centiles">
      <h5 className="caps">Chart track · BrainChart centiles</h5>
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{LIFETIME_CENTILES}</span> <span className="muted">{`${h.kind ?? 'derived'} · ${h.label ?? 'Normative-model outputs'}`}</span>
        </dd>
        <dt>Model</dt>
        <dd>
          {h.model?.name ?? 'BrainChart GAMLSS fits'}
          {h.model?.doi ? (
            <div className="muted">
              <Ref text={h.model.doi} />
            </div>
          ) : null}
          {h.model?.objects?.length ? <div className="muted mono">{`${h.model.objects.join(', ')}${h.model.repository_commit ? ` @ ${short(h.model.repository_commit)}` : ''}`}</div> : null}
        </dd>
        <dt>Output</dt>
        <dd>Derived: population centiles from published normative models, not a measurement of anyone here and not a simulation. The bands are population variation. Chart only: nothing of it is drawn in the viewport.</dd>
        <dt>Series</dt>
        <dd>{`${sim.series.length} series: ${sim.metrics.map((m) => METRIC_NAMES[m].toLowerCase()).join(', ')} for each sex, the ${sim.probabilities.map((p) => `${(p * 100).toString().replace(/\.0$/, '')}th`).join(', ')} centiles in ${h.units?.volume ?? 'mL'} at ${sim.series[0]?.age_post_conception_days.length ?? 0} ages each; between the exported ages the display interpolates linearly, and nothing is extrapolated.`}</dd>
        <dt>Age</dt>
        <dd>{`The native axis is ${h.units?.age ?? 'post-conception days'}: ${d0} to ${d1} days, shown in years as days ÷ ${h.units?.year_display?.replace(/ days$/, '') ?? '365.25'} (${ageYears(d0).toFixed(1)} to ${ageYears(d1).toFixed(0)} years). No conversion to postmenstrual age or age since birth is made (the header's mapping and birth offset are null), and the scene's clock, gestational weeks of the fetal atlas, is another scale: the chart carries no cursor.`}</dd>
        <dt>Validation</dt>
        <dd>{`ba-life's checks: every series finite, positive and ordered; the centile-to-CDF round trip's worst error ${worst ? worst.toExponential(2) : 'under 1e-13'}.`}</dd>
        {h.pipeline?.path ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`${h.pipeline.path}${h.pipeline.commit ? ` @ ${short(h.pipeline.commit)}` : ''}`}</dd>
          </>
        ) : null}
      </dl>
      {h.methods ? <p>{h.methods}</p> : null}
    </section>
  );
}

type Measure = 'area' | 'volume' | 'section' | 'assigned' | 'centiles';
/** The strip view a track leads with: Assigned thickness its sections, Model the section, the atlas its area. */
const leadMeasure = (t: Track): Measure => (t === 'assigned' ? 'assigned' : t === 'model' ? 'section' : 'area');

/** The templates' own area (cm²) or enclosed volume (mL) by source week (gestational or postmenstrual, `weeks` names it), a line a hemisphere. */
function growthModel(rows: LifetimeMetric[], end: number, origin: number, measure: 'area' | 'volume', weeks: string): ChartModel | null {
  const hemis = [...new Set(rows.map((r) => r.hemisphere))].sort();
  const unit = measure === 'area' ? 'cm²' : 'mL';
  const series: Series[] = [];
  let hi = 0;
  hemis.forEach((hemi, i) => {
    const own = rows.filter((r) => r.hemisphere === hemi && (measure === 'area' || typeof r.signed_volume_mm3 === 'number')).sort((a, b) => a.week - b.week);
    if (own.length < 2) return;
    const x = Float64Array.from(own, (r) => (r.week - origin) * WEEK_S);
    const y = Float64Array.from(own, (r) => (measure === 'area' ? r.area_mm2 / 100 : Math.abs(r.signed_volume_mm3!) / 1000));
    for (const v of y) hi = Math.max(hi, v);
    series.push({ label: prettyName(hemi), colour: SERIES[i % SERIES.length]!, x, y, style: 'line', unit });
  });
  if (!series.length) return null;
  const span = Math.floor(end / WEEK_S + 1e-9);
  // A tick every third week over the fetal fifteen; every week over the neonatal seven.
  const every = span > 8 ? 3 : 1;
  return {
    x: { domain: [0, end], format: (t) => `${Math.round(origin + t / WEEK_S)}`, ticks: Array.from({ length: span + 1 }, (_, i) => i).filter((i) => i % every === 0).map((i) => i * WEEK_S), title: weeks },
    lanes: [{ label: measure === 'area' ? 'Area' : 'Volume', unit, y: [0, hi > 0 ? hi * 1.12 : 1], ticks: 'auto', series }],
    gutter: 46,
  };
}

/** An SVG path of closed loops: RAS y along, z up (SVG's y runs down, so z is negated). */
function pathOf(loops: Float32Array[]): string {
  const parts: string[] = [];
  for (const loop of loops) {
    const n = loop.length / 3;
    if (n < 2) continue;
    let d = '';
    for (let k = 0; k < n; k++) d += `${k ? 'L' : 'M'}${loop[k * 3 + 1]!.toFixed(2)} ${(-loop[k * 3 + 2]!).toFixed(2)}`;
    parts.push(`${d}Z`);
  }
  return parts.join('');
}

/** The model's section over the atlas's own, at the clock. */
function SectionOverlay({ model, time, track }: { model: SectionsSim; time: number; track: Track }) {
  const metrics = useLoaded(() => model.metrics(), [model]);
  const [[y0, z0], [y1, z1]] = useMemo(() => model.boundsYZ, [model]);
  const pad = 4;
  const atlas = model.atlasAt(time);
  const mod = model.modelAt(time);
  const atlasPath = useMemo(() => pathOf(atlas.loops), [atlas.index, model]); // eslint-disable-line react-hooks/exhaustive-deps -- the loops are a function of the sampled index
  const modelPath = pathOf(mod.loops);
  const modelArea = metrics.value ? at(model.model.times, metrics.value.model.map((r) => numberOf(r.section_area_mm2)), time) : null;
  const atlasArea = metrics.value ? numberOf(metrics.value.atlas[atlas.index]?.all_loop_unsigned_area_sum_mm2) : null;
  const onModel = track === 'model';
  const view = `${(y0 - pad).toFixed(1)} ${(-z1 - pad).toFixed(1)} ${(y1 - y0 + 2 * pad).toFixed(1)} ${(z1 - z0 + 2 * pad).toFixed(1)}`;
  return (
    <div className="lifetime-section">
      <svg viewBox={view} preserveAspectRatio="xMidYMid meet" role="img" aria-label={`The atlas’s section at ${atlas.age} weeks and the model’s at ${mod.age.toFixed(1)} weeks, in the sagittal plane, RAS y along and z up, at one scale`}>
        <path className={`atlas${onModel ? ' faint' : ''}`} d={atlasPath} stroke={ATLAS_COLOUR} />
        <path className={`model${onModel ? '' : ' faint'}`} d={modelPath} stroke={MODEL_COLOUR} />
        <line className="scale" x1={y0 - pad + 2} y1={-z0 + pad - 2} x2={y0 - pad + 12} y2={-z0 + pad - 2} />
      </svg>
      <div className="ls-legend">
        <ul className="ls-key ba-type-small">
          <li>
            <span className="swatch" style={{ background: ATLAS_COLOUR }} />
            <span>
              <span className="lead">Measured atlas</span>
              {` · sampled ${atlas.age} wk${atlas.planeX !== null ? ` · plane x ${tick(atlas.planeX)} mm` : ''} · ${atlas.loops.length} ${atlas.loops.length === 1 ? 'loop' : 'loops'}${atlasArea !== null ? ` · ${formatValue(atlasArea / 100)} cm²` : ''}`}
            </span>
          </li>
          <li>
            <span className="swatch" style={{ background: MODEL_COLOUR }} />
            <span>
              <span className="lead">Model</span>
              {` · LIFE-014 Newton fold · ${mod.age.toFixed(1)} wk${mod.growth !== null ? ` · g ${mod.growth.toFixed(2)}` : ''}${mod.planeX !== null ? ` · plane x ${tick(mod.planeX)} mm, fixed` : ''}${modelArea !== null ? ` · ${formatValue(modelArea / 100)} cm²` : ''}${mod.exact ? '' : ' · between saved states'}`}
            </span>
          </li>
        </ul>
        <p className="ba-type-caption muted">
          RAS y along, z up, one scale, no registration: the atlas is shown at its nearest sampled week as stored (every loop), the model blended between its saved states. The bar is 10 mm. The model’s clock is assumed (g = 1 + (age − 21) / 15); LIFE-015 killed its atlas agreement, so this is a comparison, not a fit.
        </p>
      </div>
    </div>
  );
}

/** The four assigned-thickness sections at the imposed growth, on one physical scale with fixed limits: the chosen thickness in front,
 *  the others faint; a track without an accepted state at this growth draws nothing, or its last accepted state dashed when asked. */
function AssignedOverlay({ sim, growth, thickness, beyond }: { sim: ThicknessSectionsSim; growth: number; thickness: number; beyond: Beyond }) {
  const [[y0, z0], [y1, z1]] = sim.boundsYZ ?? [
    [-50, -50],
    [50, 50],
  ];
  const pad = 4;
  const chosen = sim.trackAt(thickness) ?? sim.tracks[0] ?? null;
  const rows = sim.tracks.map((t, k) => {
    const now = sim.sectionAt(t, growth);
    const last = !now && beyond === 'last' ? sim.lastAccepted(t) : null;
    return { track: t, now, last, shown: now ?? last, colour: TRACK_COLOURS[k % TRACK_COLOURS.length]!, chosen: t === chosen };
  });
  const lead = rows.find((r) => r.chosen) ?? null;
  const stateOf = (r: (typeof rows)[number]) => (r.now ? (r.now.exact ? 'exact' : 'blend') : r.last ? 'last' : r.track.frames ? 'none' : 'never');
  const view = `${(y0 - pad).toFixed(1)} ${(-z1 - pad).toFixed(1)} ${(y1 - y0 + 2 * pad).toFixed(1)} ${(z1 - z0 + 2 * pad).toFixed(1)}`;
  return (
    <div className="lifetime-section lifetime-assigned" data-growth={fmtG(growth)} data-thickness={chosen?.thicknessMm ?? ''}>
      <svg
        viewBox={view}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Four assigned-thickness sections at imposed growth ${fmtG(growth)} in the sagittal plane, RAS y along and z up, one scale; the ${chosen?.thicknessMm ?? '?'} mm track in front`}
      >
        {rows
          .filter((r) => r.shown && !r.chosen)
          .map((r) => (
            <path key={r.track.id} className={`track faint${r.last ? ' last' : ''}`} d={pathOf([r.shown!.loop])} stroke={r.colour} data-track={r.track.thicknessMm} />
          ))}
        {lead?.shown ? <path className={`track chosen${lead.last ? ' last' : ''}`} d={pathOf([lead.shown.loop])} stroke={lead.colour} data-track={lead.track.thicknessMm} /> : null}
        {lead && !lead.shown ? (
          <text className="la-none" x={(y0 + y1) / 2} y={-(z0 + z1) / 2} textAnchor="middle">
            {lead.track.frames ? `No accepted state at g ${fmtG(growth)}` : 'Never started'}
          </text>
        ) : null}
        <line className="scale" x1={y0 - pad + 2} y1={-z0 + pad - 2} x2={y0 - pad + 12} y2={-z0 + pad - 2} />
      </svg>
      <div className="ls-legend">
        <ul className="ls-key ba-type-small">
          {rows.map((r) => (
            <li key={r.track.id} className={r.chosen ? 'chosen' : ''} data-track={r.track.thicknessMm} data-state={stateOf(r)}>
              <span className="swatch" style={{ background: r.colour }} />
              <span>
                <span className="lead">{r.track.label}</span>
                {r.now
                  ? ` · g ${fmtG(growth)}${r.now.exact ? ', saved state' : `, between ${fmtG(r.track.growth[r.now.i]!)} and ${fmtG(r.track.growth[r.now.j]!)}`}${r.chosen ? ` · ${formatValue(loopArea(r.now.loop) / 100)} cm²` : ''}`
                  : r.last
                    ? ` · last accepted g ${fmtG(r.last.growth)}, shown as such · ${r.track.caseStatus.replace(/_/g, ' ')}`
                    : r.track.frames
                      ? ` · no accepted state at g ${fmtG(growth)} · last accepted g ${fmtG(r.track.lastAccepted!)} · ${r.track.caseStatus.replace(/_/g, ' ')}`
                      : ' · never started · no data'}
              </span>
            </li>
          ))}
        </ul>
        <p className="ba-type-caption muted">
          {`RAS y along, z up, one physical scale with fixed limits, no registration. Each track’s saved states are equilibria; between two the outline is blended for display, never solved; past a track’s accepted range nothing is drawn${beyond === 'last' ? ' but its last accepted state, dashed and labelled' : ''}, and nothing between thicknesses. The bar is 10 mm. Imposed growth is the model’s own axis, not age or the clock. LIFE-020: ${sim.verdict}.`}
        </p>
      </div>
    </div>
  );
}

/** The assigned-thickness run's part of the sheet (LIFE-020): the whole-set verdict first, then the run, model, axis, tracks, sections,
 *  what binds it to the registered result, the pipeline, ba-life's Methods verbatim and LIFE-020b's reviewed addendum. */
function AssignedMethods({ sim }: { sim: ThicknessSectionsSim }) {
  const h = sim.header;
  const metrics = useLoaded(() => sim.metrics(), [sim]);
  const gate = metrics.value?.gate ?? null;
  const failing = gate ? Object.entries(gate.gates).filter(([, v]) => !v).map(([k]) => k) : [];
  const sr = h.source_result ?? {};
  const planeX = sim.tracks.find((t) => t.planeX !== null)?.planeX ?? null;
  const req = sim.requested;
  return (
    <section className="methods-sim methods-lifetime-assigned">
      <h5 className="caps">Assigned thickness</h5>
      <dl>
        <dt>Verdict</dt>
        <dd className="la-verdict">
          {`LIFE-020, ${sim.verdict}${gate ? (gate.passed ? ': the whole-set numerical gate passed' : gate.passed === false ? `: the whole-set numerical gate failed${failing.length ? ` (${failing.join(', ')})` : ''}, so nothing was scored` : '') : ''}. The whole set’s registered result, copied by hash; a complete track plays even where the comparison is NOT SCORED.`}
        </dd>
        <dt>Run</dt>
        <dd>
          <span className="mono">{LIFETIME_ASSIGNED}</span> <span className="muted">{h.kind ?? 'simulated'}</span>
        </dd>
        <dt>Model</dt>
        <dd>
          {h.model?.name ?? '2D neo-Hookean/IPC Newton model'}
          {h.time_base ? <div className="muted">{h.time_base}</div> : null}
        </dd>
        <dt>Output</dt>
        <dd>Simulated: plane-strain sections’ boundaries at saved growth states, the same material nodes in every frame; between two states the outline is blended for display, not solved. No 3D surface, no age and no clock.</dd>
        <dt>Axis</dt>
        <dd>{`Imposed tangential growth g${req ? `: ${req.length} states requested, ${fmtG(req[0]!)} to ${fmtG(req[req.length - 1]!)} in steps of ${GROWTH_STEP}` : ''}; each track plays its accepted prefix alone, with no data past it and nothing between thicknesses.`}</dd>
        <dt>Tracks</dt>
        <dd>
          {sim.tracks.map((t) => (
            <div key={t.id}>{`${t.label}: ${t.frames} accepted of ${t.attemptedLoads} attempted · ${t.caseStatus.replace(/_/g, ' ')}${t.lastAccepted !== null ? ` · last accepted g ${fmtG(t.lastAccepted)}` : ''}${t.failedAt !== null ? ` · first rejected load g ${fmtG(t.failedAt)}` : ''}${t.points ? ` · ${t.points} boundary nodes` : ''}`}</div>
          ))}
        </dd>
        <dt>Sections</dt>
        <dd>{`Sagittal${planeX !== null ? `, at x ${tick(planeX)} mm` : ''}, the 21-week source plane; RAS y and z at one physical scale with fixed limits${h.display?.projection ? ` (${h.display.projection})` : ''}.`}</dd>
        {h.inputs?.length ? (
          <>
            <dt>Inputs</dt>
            <dd>
              {h.inputs
                .map((x) => `${(x as { name?: string }).name ?? (x as { id?: string }).id ?? x.source ?? x.what ?? (x as { path?: string }).path ?? 'input'}${typeof (x as { sha256?: unknown }).sha256 === 'string' ? ` (${short((x as { sha256: string }).sha256)})` : ''}`)
                .join('; ')}
            </dd>
          </>
        ) : null}
        <dt>Bound to</dt>
        <dd className="mono muted">{`result ${short(sr.result_sha256) || '?'} · gate ${short(sr.numerical_gate_sha256) || '?'} · protocol ${short(sr.protocol_sha256) || '?'} · producer ${short(sr.simulation_producer_commit) || '?'}`}</dd>
        {h.pipeline?.path ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`${h.pipeline.path}${h.pipeline.commit ? ` @ ${short(h.pipeline.commit)}` : ''}`}</dd>
          </>
        ) : null}
      </dl>
      {h.methods ? <p>{h.methods}</p> : null}
      <p className="la-addendum">{LIFE_020B_ADDENDUM}</p>
    </section>
  );
}

/** The strip under the viewport: the atlas's growth by week with the cursor at the clock, the section overlay, or the assigned
 *  thicknesses' sections. */
export function LifetimeTraces({ main }: { main: SimSlot }) {
  const sim = useSequence();
  const model = useModel();
  const assigned = useAssigned();
  const growth = useLifetime((s) => s.growth);
  const thickness = useLifetime((s) => s.thickness);
  const beyond = useLifetime((s) => s.beyond);
  const tl = useAtlas(timelineOf);
  const time = useAtlas((s) => s.time);
  const setTime = useAtlas((s) => s.setTime);
  const track = useLifetime((s) => s.track);
  const chartKind = useLifetime((s) => s.chart);
  const setChart = useLifetime((s) => s.setChart);
  const metric = useLifetime((s) => s.metric);
  const setMetric = useLifetime((s) => s.setMetric);
  const centiles = useLifetime((s) => s.centiles);
  const centilesStatus = useLifetime((s) => s.centilesStatus);
  const centilesWhy = useLifetime((s) => s.centilesWhy);
  // The track leads the strip: Model opens the section, Measured atlas the area; the tabs stay free after that. A link's
  // track (`track=model`) is set before the strip mounts, so the strip starts from it as well as following its changes. The
  // Centiles tab is the store's `chart` (a link's `chart=centiles`), so it travels and survives a remount.
  const [measure, setMeasure] = useState<Measure>(() => (chartKind === 'centiles' ? 'centiles' : leadMeasure(track)));
  const lastTrack = useRef(track);
  useEffect(() => {
    if (lastTrack.current === track) return;
    lastTrack.current = track;
    if (chartKind !== 'centiles') setMeasure(leadMeasure(track));
  }, [track, chartKind]);
  useEffect(() => {
    if (chartKind === 'centiles') {
      setMeasure('centiles');
      void openCentiles();
    } else setMeasure((m) => (m === 'centiles' ? (leadMeasure(track)) : m));
  }, [chartKind, track]);
  const pick = (v: Measure) => {
    setMeasure(v);
    setChart(v === 'centiles' ? 'centiles' : 'auto');
  };
  const rows = useLoaded(() => (sim ? sim.metrics() : null), [sim]);
  const neonatal = isNeonatal(sim?.header);
  // The section needs the model's run; without it the strip shows the area, as the panel shows the measured atlas.
  const onCentiles = measure === 'centiles';
  const onSection = measure === 'section' && !!model;
  const onAssigned = measure === 'assigned' && !!assigned;
  const chart = onCentiles || onSection || onAssigned ? null : measure === 'section' || measure === 'assigned' ? 'area' : (measure as 'area' | 'volume');
  const chartModel = useMemo(() => (chart && rows.value && tl ? growthModel(rows.value, tl.duration, tl.origin ?? 0, chart, ageShort(sim?.header)) : null), [rows.value, tl, chart, sim]);
  const centilesChart = useMemo(() => (onCentiles && centiles ? centilesModel(centiles, metric) : null), [onCentiles, centiles, metric]);
  const hemis = useMemo(() => [...new Set((rows.value ?? []).map((r) => r.hemisphere))].sort(), [rows.value]);
  const options: SegmentOption<Measure>[] = [
    { value: 'area', label: 'Area' },
    { value: 'volume', label: 'Volume' },
    ...(model ? [{ value: 'section' as const, label: 'Section', hint: 'The model’s section over the atlas’s own' }] : []),
    ...(assigned ? [{ value: 'assigned' as const, label: 'Thickness', hint: 'LIFE-020’s assigned thicknesses: four sections at a growth of their own' }] : []),
    { value: 'centiles', label: 'Centiles', hint: 'BrainChart’s volume centiles across a lifetime, by sex' },
  ];
  const tabs = (
    <>
      <Segmented size="sm" label="Measure" value={onCentiles ? 'centiles' : onSection ? 'section' : onAssigned ? 'assigned' : (chart ?? 'area')} options={options} onChange={pick} />
      {onCentiles ? <Segmented size="sm" label="Metric" value={metric} options={METRIC_OPTIONS} onChange={(m: CentileMetric) => setMetric(m)} /> : null}
    </>
  );
  const [d0, d1] = centiles?.ageRange ?? [115, 36525];
  return (
    <Frame
      title={onCentiles ? 'Brain volume across a lifetime' : onAssigned ? 'Assigned thickness against growth' : onSection ? 'Measured atlas against model' : (main.header.scenario?.label ?? (neonatal ? 'Neonatal white matter · dHCP atlas' : 'Fetal white matter · dHCP atlas'))}
      sub={
        onCentiles
          ? 'BrainChart centiles, mL · 2.5th, 50th, 97.5th by sex · post-conception years (days ÷ 365.25) · no cursor'
          : onAssigned
            ? 'LIFE-020: four assigned layer thicknesses, sagittal sections, RAS mm · imposed growth g, the model’s own axis, no clock'
            : onSection
              ? 'the atlas’s own section and LIFE-014’s Newton fold, sagittal, RAS mm'
            : measure === 'area'
              ? `white-matter surface area, cm² · the templates’ own, by ${perWeek(sim?.header)}`
              : `enclosed volume, mL · the templates’ own, by ${perWeek(sim?.header)}`
      }
      // The Centiles header (its title, metric control and key) is too wide for one row at 1300 px: its controls take a second row.
      wrap={onCentiles}
      controls={tabs}
      keyBar={
        onCentiles ? (
          <SwatchKey
            items={[
              { label: 'Female', colour: SEX_COLOURS.Female },
              { label: 'Male', colour: SEX_COLOURS.Male },
            ]}
          />
        ) : !onSection && !onAssigned && hemis.length ? (
          <SwatchKey items={hemis.map((h, i) => ({ label: prettyName(h), colour: SERIES[i % SERIES.length]! }))} />
        ) : null
      }
    >
      {onCentiles ? (
        centilesChart ? (
          <>
            <Chart label={`${METRIC_NAMES[metric]} centiles by post-conception age`} model={centilesChart} height={HEIGHT} />
            <p className="ba-type-caption muted centiles-note">
              {`${METRIC_NAMES[metric]}: each sex’s 2.5th–97.5th centile band with its median, Bethlehem 2022’s original fits, ${centiles?.series[0]?.age_post_conception_days.length ?? 0} ages from ${d0} to ${d1} post-conception days (${ageYears(d0).toFixed(1)}–${ageYears(d1).toFixed(0)} years). Population variation, not anyone here; the native age is kept, with no conversion to postmenstrual age or age since birth, so the scene’s clock (${ageWords(sim?.header)}) draws no cursor here.`}
            </p>
          </>
        ) : (
          <div className="traces-empty">{centilesStatus === 'error' ? (centilesWhy ?? 'The centiles didn’t open.') : 'Reading BrainChart’s centiles…'}</div>
        )
      ) : onAssigned && assigned ? (
        <AssignedOverlay sim={assigned} growth={growth} thickness={thickness} beyond={beyond} />
      ) : onSection && model ? (
        <SectionOverlay model={model} time={time} track={track} />
      ) : chartModel ? (
        <Chart label={`White-matter surface area and volume by ${perWeek(sim?.header)}`} model={chartModel} height={HEIGHT} cursor={sceneTime} onSeek={(x) => setTime(x)} />
      ) : (
        <div className="traces-empty">{rows.done ? 'The run has no metrics to chart.' : 'Reading the atlas’s metrics…'}</div>
      )}
    </Frame>
  );
}

// Cortical thickness (lifetime-thickness-v0; docs/contracts/lifetime-thickness-v0.md): the Age and Thickness sections, the Methods
// section and the strip for Lifetime's still view on fsaverage's Destrieux cortex (features/lifetime.ts drives the layer).

const SEX_OPTIONS: SegmentOption<ThicknessSex>[] = [
  { value: 'Female', label: 'Female' },
  { value: 'Male', label: 'Male' },
];
const TH_MODE_OPTIONS: SegmentOption<ThicknessMode>[] = [
  { value: 'age', label: 'One age', hint: 'The expected thickness at one age' },
  { value: 'compare', label: 'Two ages', hint: 'B − A: the difference of the published medians between two ages, parcel by parcel' },
];

function thStatusLine(status: ThicknessStatus, why: string | null, mode: ThicknessMode): string {
  if (status === 'loading') return 'Reading the reference…';
  if (status === 'none' || status === 'error') return why ?? 'The viewport refused the layer.';
  if (status === 'on')
    return mode === 'compare'
      ? 'The cortex carries the difference of the two ages’ medians, parcel by parcel; vertices outside the parcellation keep the tissue’s own shade.'
      : 'The cortex carries the parcel medians at this age; vertices outside the parcellation keep the tissue’s own shade.';
  return '';
}

/** A parcel's name for the panel: its annotation name in words, with its hemisphere. */
function parcelName(parcels: ThicknessParcel[] | null | undefined, modelIndex: number, modelIds: string[]): string {
  const p = parcels?.find((x) => x.model_index === modelIndex);
  const id = modelIds[modelIndex] ?? '';
  const side = (p?.hemisphere ?? id.slice(0, 2)) === 'lh' ? 'left' : 'right';
  const name = p?.annotation_name ?? (id ? id.replace(/^[lr]h_/, '').replace(/_thickness$/, '') : `parcel ${modelIndex}`);
  return `${name.replace(/_and_/g, ' & ').replace(/_/g, ' ')}, ${side}`;
}
const mm = (v: number, digits = 2): string => `${v.toFixed(digits)} mm`;

function ThicknessSections() {
  const sim = useLifetime((s) => s.th);
  const mode = useLifetime((s) => s.thMode);
  const age = useLifetime((s) => s.thAge);
  const ageB = useLifetime((s) => s.thAgeB);
  const sex = useLifetime((s) => s.thSex);
  const status = useLifetime((s) => s.thStatus);
  const why = useLifetime((s) => s.thWhy);
  const reply = useLifetime((s) => s.thReply);
  const setMode = useLifetime((s) => s.setThMode);
  const setAge = useLifetime((s) => s.setThAge);
  const setAgeB = useLifetime((s) => s.setThAgeB);
  const setSex = useLifetime((s) => s.setThSex);
  const parcels = useLoaded(() => (sim ? sim.parcels() : null), [sim]);
  // The per-parcel values shown: the medians at the age, or B − A of the medians at two ages (the same subtraction the layer draws).
  const values = useLoaded(
    () =>
      sim
        ? mode === 'compare'
          ? Promise.all([sim.parcelMedians(sex, age), sim.parcelMedians(sex, ageB)]).then(([a, b]) => Float32Array.from(a, (v, i) => b[i]! - v))
          : sim.parcelMedians(sex, age)
        : null,
    [sim, mode, sex, age, ageB],
  );
  const sum = values.value ? parcelSummary(values.value) : null;
  const compare = mode === 'compare';
  const range: [number, number] = compare ? THICKNESS_DIFF_RANGE : (sim?.range ?? [1.5, 4]);
  const [a0, a1] = THICKNESS_AGES;
  const name = (i: number) => parcelName(parcels.value, i, sim?.modelIds ?? []);
  const line = thStatusLine(status, why, mode);
  return (
    <>
      <Section title="Age">
        <p className="lifetime-age" aria-live="off">
          <strong className="num">{age}</strong>
          {compare ? (
            <>
              {' → '}
              <strong className="num">{ageB}</strong>
            </>
          ) : null}{' '}
          years since birth
        </p>
        <div className="pd-step th-age">
          <span className="value ba-type-small">{a0}</span>
          <Slider value={age} min={a0} max={a1} step={1} onChange={(v) => setAge(v)} label={compare ? 'Age A' : 'Age'} />
          <span className="value ba-type-small">{a1}</span>
        </div>
        {compare ? (
          <div className="pd-step th-age-b">
            <span className="value ba-type-small">{a0}</span>
            <Slider value={ageB} min={a0} max={a1} step={1} onChange={(v) => setAgeB(v)} label="Age B" />
            <span className="value ba-type-small">{a1}</span>
          </div>
        ) : null}
        <div className="rows">
          <div className="row">
            <span>Sex</span>
            <Segmented size="sm" label="Sex" value={sex} options={SEX_OPTIONS} onChange={(v: ThicknessSex) => setSex(v)} />
          </div>
          <div className="row">
            <span>Showing</span>
            <Segmented size="sm" label="Ages" value={mode} options={TH_MODE_OPTIONS} onChange={(v: ThicknessMode) => setMode(v)} />
          </div>
        </div>
        <p className="live-hint">
          {compare
            ? `${THICKNESS_DIFF_LINE} Years since birth, 2 to 100, the view’s own control: no clock, and nothing maps these ages to the fetal weeks or to BrainChart’s post-conception age.`
            : 'The normative model’s predictive median at this age: a population expectation, not anyone here. Years since birth, 2 to 100, the view’s own control: no clock, and nothing maps this age to the fetal weeks or to BrainChart’s post-conception age.'}
        </p>
      </Section>
      <Section title="Thickness">
        <div
          className="colorbar lifetime-legend th-legend"
          role="img"
          aria-label={compare ? `Change in expected thickness, B − A, ${tick(range[0])} to ${tick(range[1])} mm, as colour over the cortex` : `Expected cortical thickness, ${tick(range[0])} to ${tick(range[1])} mm, as colour over the cortex`}
        >
          <span className="name ba-type-small">{compare ? 'Change · B − A · mm' : 'Expected thickness · mm'}</span>
          <span className="strip" style={{ background: gradientCss(colormapStops(compare ? 'coolwarm' : 'viridis')) }} />
          <span className="ticks ba-type-caption">
            <span>{compare ? `${tick(range[0])} thinner` : `${tick(range[0])} mm`}</span>
            <span>{compare ? `+${tick(range[1])} thicker` : `${tick(range[1])} mm`}</span>
          </span>
        </div>
        <dl className="dfc-facts pd-facts th-facts">
          <div>
            <dt>{compare ? 'Mean change' : 'Mean of parcels'}</dt>
            <dd className="th-mean">{sum && Number.isFinite(sum.mean) ? (compare ? `${signed(sum.mean, 2)} mm` : mm(sum.mean)) : '—'}</dd>
          </div>
          <div>
            <dt>{compare ? 'Most thinned' : 'Thickest'}</dt>
            <dd className="th-extreme">{sum && values.value && sum.finite ? (compare ? `${name(sum.min)} ${signed(values.value[sum.min]!, 2)}` : `${name(sum.max)} ${mm(values.value[sum.max]!)}`) : '—'}</dd>
          </div>
          <div>
            <dt>{compare ? 'Least thinned' : 'Thinnest'}</dt>
            <dd className="th-extreme">{sum && values.value && sum.finite ? (compare ? `${name(sum.max)} ${signed(values.value[sum.max]!, 2)}` : `${name(sum.min)} ${mm(values.value[sum.min]!)}`) : '—'}</dd>
          </div>
          <div>
            <dt>Parcels</dt>
            <dd className="th-count">{sim && sum ? `${sum.finite} of ${sim.parcelCount}` : '—'}</dd>
          </div>
          <div>
            <dt>Vertices</dt>
            <dd className="th-vertices">{sim ? `${(reply?.covered ?? sim.vertices).toLocaleString()} of ${(reply?.of ?? sim.vertices).toLocaleString()} drawn · ${sim.noDataVertices.toLocaleString()} no data` : '—'}</dd>
          </div>
        </dl>
        <p className="live-hint">
          {sim
            ? `Rutherford 2022’s predictive medians per Destrieux parcel (${sim.parcelCount}), expanded over fsaverage’s own vertices exactly: no transfer, nearest parcel or registration; the ${sim.noDataVertices.toLocaleString()} vertices outside the parcellation keep the tissue’s own shade. Between two annual medians the value is linear, a derived display. The mean is over the parcels with equal weight, a summary of the reference, not a published statistic.`
            : 'Rutherford 2022’s predictive medians per Destrieux parcel, expanded over fsaverage’s own vertices exactly.'}
        </p>
        {line ? (
          <p className={`live-hint lifetime-status th-status ${status}`} aria-live="polite">
            {line}
          </p>
        ) : null}
      </Section>
    </>
  );
}

/** The view's part of the sheet: the run, its pack, the model, what's shown and the mesh's own warnings, with ba-life's summary verbatim. */
function ThicknessMethods() {
  const sim = useLifetime((s) => s.th);
  const status = useLifetime((s) => s.thStatus);
  const why = useLifetime((s) => s.thWhy);
  const mode = useLifetime((s) => s.thMode);
  const age = useLifetime((s) => s.thAge);
  const ageB = useLifetime((s) => s.thAgeB);
  const sex = useLifetime((s) => s.thSex);
  if (!sim) {
    return (
      <section className="methods-sim methods-lifetime-thickness">
        <h5 className="caps">Expected cortical thickness</h5>
        <dl>
          <dt>Run</dt>
          <dd>
            <span className="mono">{LIFETIME_THICKNESS}</span>
          </dd>
          <dt>Drawn</dt>
          <dd>{status === 'error' ? (why ?? 'Not read.') : 'Reading the reference…'}</dd>
        </dl>
      </section>
    );
  }
  const h = sim.header;
  const m = h.model ?? {};
  const methods = typeof h.methods === 'string' ? { summary: h.methods, references: [] as string[] } : (h.methods ?? {});
  const surfaces = h.surfaces ?? [];
  const sideOf = (id: string) => (id.includes('left') ? 'left' : 'right');
  return (
    <section className="methods-sim methods-lifetime-thickness">
      <h5 className="caps">Expected cortical thickness</h5>
      <dl>
        <dt>Run</dt>
        <dd>
          <span className="mono">{LIFETIME_THICKNESS}</span> <span className="muted">{h.kind ?? 'reference'}</span>
        </dd>
        {h.pack?.id ? (
          <>
            <dt>Pack</dt>
            <dd>
              <span className="mono">{h.pack.id}</span> <span className="muted mono">{h.pack.version ?? ''}</span>
              <span className="muted">{` · fsaverage’s Destrieux cortex at native resolution, in ${h.coordinates?.frame ?? h.space ?? 'its own space'}`}</span>
            </dd>
          </>
        ) : null}
        <dt>Model</dt>
        <dd>
          {m.name ?? 'Rutherford et al. 2022'}
          {m.type ? <div className="muted">{`${m.type}${m.pcntoolkit ? ` · PCNtoolkit ${m.pcntoolkit}` : ''}${m.commit ? ` · ${m.commit.slice(0, 8)}` : ''}`}</div> : null}
          {m.site_convention ? <div className="muted">{m.site_convention}</div> : null}
        </dd>
        <dt>Output</dt>
        <dd>{`Reference: the model’s ${h.statistic ?? 'predictive median'} of cortical thickness per Destrieux parcel at each age, for each sex, expanded over the pack’s vertices exactly. Nothing here is measured on anyone or simulated: a normative expectation, with no validation on a new cohort.`}</dd>
        <dt>Ages</dt>
        <dd>{`${sim.ageRange[0]}–${sim.ageRange[1]} years since birth, ${sim.ages.length} annual medians; between two the value is linear, a derived display. The age is this view’s own control, never a clock, and nothing maps it to the fetal sequence’s gestational weeks or to BrainChart’s post-conception age.`}</dd>
        <dt>Shown</dt>
        <dd>
          {mode === 'compare'
            ? `${sex}, ${ageB} − ${age} years: the difference of published normative medians, not individual change or a forecast, on a diverging scale from ${THICKNESS_DIFF_RANGE[0]} to +${THICKNESS_DIFF_RANGE[1]} mm centred on zero.`
            : `${sex} at ${age} years, over ${sim.range[0]}–${sim.range[1]} mm.`}
        </dd>
        <dt>Parcels</dt>
        <dd>{`${sim.parcelCount} Destrieux parcels, one exact model index per vertex (${sim.vertices.toLocaleString()} over ${surfaces.map((x) => x.id).join(' and ')}); the ${sim.noDataVertices.toLocaleString()} vertices outside the parcellation (${surfaces.map((x) => `${(x.no_data_vertices ?? 0).toLocaleString()} ${sideOf(x.id)}`).join(', ')}) are NaN and keep the tissue’s own colour. No DKT transfer, nearest parcel or registration.`}</dd>
        <dt>Mesh</dt>
        <dd>{`fsaverage at native resolution, undecimated (${surfaces.map((x) => `${x.vertices.toLocaleString()} vertices${x.triangles ? `, ${x.triangles.toLocaleString()} triangles` : ''} ${sideOf(x.id)}`).join('; ')}); the source’s two zero-area faces per hemisphere are kept and every vertex normal is defined (the pack’s QC).`}</dd>
        <dt>Drawn</dt>
        <dd>{status === 'on' ? 'In the 3D viewport, through a live vertex layer on the two cortex structures in their own vertex order.' : status === 'none' ? (why ?? 'Not in this viewport.') : status === 'error' ? (why ?? 'Refused by the viewport.') : 'Loading.'}</dd>
        {h.pipeline?.path ? (
          <>
            <dt>Pipeline</dt>
            <dd className="mono">{`${h.pipeline.path}${h.pipeline.commit ? ` @ ${h.pipeline.commit.slice(0, 8)}` : ''}`}</dd>
          </>
        ) : null}
      </dl>
      {methods.summary ? <p>{methods.summary}</p> : null}
      {methods.references?.length ? (
        <p className="muted">
          {methods.references.map((r, i) => (
            <span key={r}>
              {i ? ' · ' : ''}
              <Ref text={r} />
            </span>
          ))}
        </p>
      ) : null}
    </section>
  );
}

/** The strip in the Cortical thickness view: the mean of the parcel medians by age, a line a sex, the chosen age or ages marked; a
 *  click moves age A. The x axis is age in years since birth, never the scene's clock. */
export function ThicknessTraces() {
  const sim = useLifetime((s) => s.th);
  const mode = useLifetime((s) => s.thMode);
  const age = useLifetime((s) => s.thAge);
  const ageB = useLifetime((s) => s.thAgeB);
  const sex = useLifetime((s) => s.thSex);
  const setAge = useLifetime((s) => s.setThAge);
  const curves = useLoaded(() => (sim ? Promise.all([sim.meanCurve('Female'), sim.meanCurve('Male')]) : null), [sim]);
  const model = useMemo((): ChartModel | null => {
    if (!sim || !curves.value) return null;
    const x = Float64Array.from(sim.ages);
    let lo = Infinity;
    let hi = -Infinity;
    for (const c of curves.value)
      for (const v of c) {
        lo = Math.min(lo, v);
        hi = Math.max(hi, v);
      }
    const pad = (hi - lo) * 0.15 || 0.1;
    const series: Series[] = [
      { label: 'Female', colour: SEX_COLOURS.Female, x, y: curves.value[0], style: 'line', unit: 'mm', alpha: sex === 'Female' ? 1 : 0.45 },
      { label: 'Male', colour: SEX_COLOURS.Male, x, y: curves.value[1], style: 'line', unit: 'mm', alpha: sex === 'Male' ? 1 : 0.45 },
    ];
    const [a0, a1] = sim.ageRange;
    return {
      x: { domain: [a0, a1], format: (v) => `${Math.round(v)}`, ticks: [2, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].filter((t) => t >= a0 && t <= a1) },
      lanes: [{ label: 'Mean of the parcel medians by age, years since birth', unit: 'mm', y: [Math.max(0, lo - pad), hi + pad], ticks: 'auto', series }],
      markers:
        mode === 'compare'
          ? [
              { x: age, colour: ATLAS_COLOUR, label: `A · ${age}` },
              { x: ageB, colour: MODEL_COLOUR, label: `B · ${ageB}` },
            ]
          : [{ x: age, colour: ATLAS_COLOUR, label: `${age}` }],
      gutter: 46,
    };
  }, [sim, curves.value, mode, age, ageB, sex]);
  return (
    <Frame
      title="Expected cortical thickness"
      sub={`mean of ${sim?.parcelCount ?? 148} parcel medians, mm · years since birth · Rutherford 2022, by sex${mode === 'compare' ? ' · A and B marked' : ''}`}
      keyBar={
        <SwatchKey
          items={[
            { label: 'Female', colour: SEX_COLOURS.Female },
            { label: 'Male', colour: SEX_COLOURS.Male },
          ]}
        />
      }
    >
      {model ? <Chart label="Mean expected cortical thickness by age, by sex" model={model} height={HEIGHT} onSeek={(x) => setAge(x)} /> : <div className="traces-empty">{sim ? 'Summing the parcels…' : 'Reading the reference…'}</div>}
    </Frame>
  );
}
